import { BaseService } from './BaseService';
import { CompanyService } from './CompanyService';
import DateHelper from '../commons/DateHelper';
import Constants, { orderPaymentTypes } from '../commons/Constants';
import PhotoUploader from '../commons/PhotoUploader';
import Collections from '../commons/Collections';
import moment from 'moment';

export class BranchOfficeService extends BaseService {
	async getBranchOfficeData(id) {
		const snapshot = await this.getDocRef(`${Collections.branchOffices}/${id}`).get();
		const data = snapshot.data();
		return {
			id: snapshot.id,
			name: data.name,
			photo: data.photo,
			photoRef: data.photo_ref,
			minimun: data.minimun,
			status: data.status,
			shippingCost: data.shippingCost,
			type: data.type,
			provinces: data.provinces,
			location: data.location,
			companyId: data.company_id,
			address: data.address,
			total_orders: data.Total_orders,
			zones: data.zones || []
		};
	}

	async update(id, data) {
		const response = {
			ok: false,
			message: ''
		};
		try {
			await this.getDocRef(`${Collections.branchOffices}/${id}`).update(data);
			this.ok = true;
		} catch (error) {
			response.message = error.message;
		}
		return response;
	}

	async delete(id) {
		const response = {
			ok: false,
			message: ''
		};
		try {
			await this.getDocRef(`${Collections.branchOffices}/${id}`).delete();
			this.ok = true;
		} catch (error) {
			response.message = error.message;
		}
		return response;
	}

	async getBranchOffices(companyId) {
		const response = {
			ok: false,
			message: '',
			branchOffices: []
		};
		try {
			let query = this.getCollectionRef(Collections.branchOffices);
			if (companyId) {
				if(Array.isArray(companyId)){
					query = query.where('company_id', 'in', companyId);
				}else{
					query = query.where('company_id', '==', companyId);
				}
			}
			const snapshot = await query.get();
			response.branchOffices = snapshot.docs.map((x) => {
				const data = x.data();
				return {
					id: x.id,
					name: data.name,
					photo: data.photo,
					photoRef: data.photo_ref,
					minimum: data.minimum,
					status: data.status,
					shippingCost: data.shipping_cost,
					type: data.type,
					provinces: data.provinces,
					location: data.location,
					companyId: data.company_id,
					address: data.address,
					total_orders: data.Total_orders,
					loading: false,
					shortName: data.short_name || '',
					backgroundPhoto: data.background_photo || ''
				};
			});
			response.ok = true;
		} catch (error) {
			response.message = error.message;
		}
		return response;
	}

	async getBranchOfficeOrders(branchOfficeId) {
    try {
      return this.http(`app/companies/${branchOfficeId}/orders`, 'get', {});
    } catch (error) {
      return {
        ok: false,
        message: error.message
      }
    }
	}

	async getOrdersByCompany(branchOfficeId) {
		const response = {
			ok: false,
			message: '',
			orders: []
		};
		try {
			const snapshot = await this.getCollectionRef(`${Collections.branchOffices}/${branchOfficeId}/${Collections.orders}`).get();
			response.ok = true;
			response.orders = mapOrdersSnapshotToOrderObjects(snapshot.docs);
		} catch (error) {
			response.message = error.message;
		}
		return response;
	}

	async changeOrderStatus(branchOfficeId, orderId, newStatus, appClientId) {
		console.log('==> (changeOrderStatus) ', branchOfficeId, orderId, newStatus, appClientId);
		const response = {
			ok: false,
			message: ''
		};
		try {
			await this.getTransaction(async (transaciton) => {
				if (appClientId) {
					const clientOrderRef = this.getDocRef(
						`${Collections.users}/${appClientId}/${Collections.history}/${orderId}`
					);
					await transaciton.update(clientOrderRef, {
						status: newStatus
					});
				}
				const path = `${Collections.branchOffices}/${branchOfficeId}/${Collections.orders}/${orderId}`;
				console.log('path ', path);
				const orderRef = this.getDocRef(path);
				await transaciton.update(orderRef, {
					status: newStatus
				});
			});
			response.ok = true;
		} catch (error) {
			response.message = error.message;
		}
		return response;
	}

	async getOrderCart(branchOfficeId, orderId) {
		const response = {
			ok: false,
			message: '',
			products: []
		};
		try {
			const result = await this.httpPost('/getOrderDetail', {
				company_id: branchOfficeId,
				order_id: orderId
			});
			return result;
		} catch (error) {
			response.message = error.message;
		}
		return response;
	}

	mapOrdersSnapshotToOrderObjects(docs, branchOfficeData) {
		return docs.map((x) => {
			const data = x.data();
			return this.createOrderObject(x, branchOfficeData);
		});
	}

	async saveBranchOffice(data, mainPhotoFile, bgPhotoFile, mainPhotoProgressCallback, bgPhotoProgressCallback) {
		const response = {
			ok: false,
			message: ''
		};
		try {
			const branchOfficesCollectionRef = this.getCollectionRef(Collections.branchOffices);
			//Si es nueva
			if (!data.id) {
				const newId = this.generateIdFromName(data.name);
				const branchO = await branchOfficesCollectionRef.doc(newId).get();

				if (branchO.exists) {
					response.message = 'Ya existe una sucursal con ese nombre';
					return response;
				} else {
					data.id = newId;
				}
			}
			const branchOfficeDocRef = branchOfficesCollectionRef.doc(data.id);
			await branchOfficeDocRef.set(data, { merge: true });

			const companyService = new CompanyService();
			await companyService.saveCompany({
				id: data.companyId,
				minimum: data.minimum ? Number(data.minimum) : 0,
				shipping_cost: Number(data.shippingCost)
			});

			//Foto principal
			if (mainPhotoFile) {
				const photoUpload = await PhotoUploader.upload(
					'BranchOffices',
					data.id,
					mainPhotoFile,
					mainPhotoProgressCallback
				);
				await branchOfficeDocRef.set(
					{
						photo: photoUpload.url,
						photo_ref: photoUpload.fileNameRef
					},
					{ merge: true }
				);
			}

			//Foto de fondo
			if (bgPhotoFile) {
				const photoUpload = await PhotoUploader.upload(
					'BranchOfficesBG',
					data.id,
					bgPhotoFile,
					bgPhotoProgressCallback
				);
				await branchOfficeDocRef.set(
					{
						background_photo: photoUpload.url,
						background_photo_ref: photoUpload.fileNameRef
					},
					{ merge: true }
				);
			}

			response.ok = true;
		} catch (error) {
			response.message = error.message;
		}
		return response;
	}

	generateIdFromName(name) {
		return name.trim().toLowerCase().replace(/\s/gi, '') + '@gmail.com';
	}

	getStatusLabel(status) {
		let label = status;
		switch (parseInt(status)) {
			case Constants.orderStatus.NEW:
				label = '<span class="badge badge-primary">Reciente</span>';
				break;
			case Constants.orderStatus.IN_REPART:
				label = '<span class="badge badge-warning">En reparto</span>';
				break;
			case Constants.orderStatus.SHIPPED:
				label = '<span class="badge badge-info">Enviada</span>';
				break;
			case Constants.orderStatus.ARRAIVED:
				label = '<span class="badge badge-success">Entregado</span>';
				break;
			case Constants.orderStatus.CANCELED:
				label = '<span class="badge badge-danger">Cancelada</span>';
				break;
			case Constants.orderStatus.RECIVED:
				label = '<span class="badge badge-info">Recibida</span>';
				break;
			case Constants.orderStatus.TEST:
				label = '<span class="badge badge-primary">Pagada pruebas</span>';
				break;
		}
		return label;
	}

	createOrderObject(orderSnapshot, branchOfficeData) {
		const orderData = orderSnapshot.data();
		const total = orderData.total;
		const subtotal = orderData.invoice_info ? orderData.invoice_info.sub_total : orderData.sub_total;
		const pointB = orderData.service_route ? orderData.service_route.point_b : {};
		const { dir = '', province = '', mobile_number = '', ref = '' } = pointB;
		return {
			id: orderSnapshot.id,
			number: orderData.total_orders || 'N/D',
			clientName: orderData.client,
			clientPhone: mobile_number,
			clientAddress: dir,
			clientProvince: province,
			clientAddressRef: ref,
			date: DateHelper.format(orderSnapshot.id),
			confirmationDate: orderData.confirmation_date,
			// service_fee: orderData.service_fee,
			// service_fee_calculated: orderData.service_fee_calculated,
			total: total,
			subtotal: subtotal,
			status: orderData.status,
			statusLabel: this.getStatusLabel(orderData.status),
			branchOfficeId: branchOfficeData.id,
			companyId: branchOfficeData.company_id || branchOfficeData.companyId,
			branchOfficePhoto: branchOfficeData.photo,
			branchOfficeName: branchOfficeData.name,
			branchOfficeCurrency: branchOfficeData.currency,
			loading: false,
			paymentType: orderData.payment_type,
			transactionId: orderData.transaction_id,
			paymentProvider: orderData.payment_provider,
			note: orderData.note || '',
			totalInCoupon: orderData.total_in_coupon,
			appliedCoupon: orderData.applied_coupon,
			shippingCost: orderData.shipping_cost,
			userId: orderData.user_id,
			availableForDelivery: orderData.available_to_take,
			pickup: orderData.pickup,
			scheduleOrder: orderData.schedule_order,
			paidAt: this.getPaymentDate(orderData)
		};
	}

	getPaymentDate(orderData){
		if(orderData.paid_at){
			return orderData.paid_at;
		}
		if(orderData.payment_type === orderPaymentTypes.STRIPE){
			if(orderData.payment_intent && orderData.payment_intent.status === 'succeeded'){
				return moment.unix(orderData.payment_intent.created).format('hh:mm a DD/MM/YYYY');
			}
		}
		return orderData.paid_at
	}
	
	async cancelOrder(branchOfficeId, orderId, cancelReason) {
		return this.httpPost('app/cancelOrder', {
			company_id: branchOfficeId,
			order_id: orderId,
			cancel_reason: cancelReason
		});
	}

	async listenForOrders(companyId, callback) {
		const branchOfficesCollectionRef = this.getCollectionRef(`${Collections.branchOffices}`);
		if (companyId) {
			
			let query = branchOfficesCollectionRef.where('company_id', '==', companyId);
			if(Array.isArray(companyId)){
				query = branchOfficesCollectionRef.where('company_id', 'in', companyId);
			}
			const userBranchOffices = await query.get();

			userBranchOffices.docs.forEach((branchOfficeSnapshot) => {
				const branchOfficeObj = this.createBranchOfficeObject(branchOfficeSnapshot);

				branchOfficesCollectionRef
					.doc(branchOfficeSnapshot.id)
					.collection(Collections.orders)
					.where('status', '==', Constants.orderStatus.NEW)
					.onSnapshot((snap) => {
						snap.docs.forEach((x) => {
							const order = this.createOrderObject(x, branchOfficeObj);
							callback(order);
						});
					});
			});

		} else {
			// ADMINS y DELIVERIES
			const branchOffices = await branchOfficesCollectionRef.get();
			branchOffices.docs.forEach((branchOfficeSnapshot) => {
				const branchOfficeObj = this.createBranchOfficeObject(branchOfficeSnapshot);
				branchOfficesCollectionRef
					.doc(branchOfficeSnapshot.id)
					.collection(Collections.orders)
					.where('status', 'in', [
						Constants.orderStatus.NEW,
						20,
					])
					.onSnapshot((snap) => {
						snap.docs.forEach((x) => {
							const order = this.createOrderObject(x, branchOfficeObj);
							callback(order);
						});
					});
			});
		}
	}

	createBranchOfficeObject(branchOfficeSnapshot) {
		const data = branchOfficeSnapshot.data();
		return {
			id: branchOfficeSnapshot.id,
			status: data.status,
			name: data.name,
			photo: data.photo || '',
			photoRef: data.photo_ref || '',
			provinces: data.provinces,
			totalOrders: data.Total_orders || 0,
			type: data.type,
			minimum: data.minimum,
			shippingCost: data.shipping_cost,
			location: data.location,
			companyId: data.company_id || '',
			address: data.address || '',
			currency: data.currency || '',
			companyId: data.company_id
		};
	}

	getOrderDetail(branchOfficeId, orderId){
		return this.httpPost('getOrderDetail', {
			company_id: branchOfficeId,
			order_id: orderId
		});
	}
}
